import { SubmissionsPanel } from './SubmissionsPanel';
import { EnvironmentInventory } from './EnvironmentInventory';
import { ProjectionPanel } from './ProjectionPanel';
import type { ExternalJob } from './sync-status';
import { useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOnline } from '@ndrc/app-sdk/client';
import { EmptyState, Icon } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { callCore, mutateCore, requestExternalAccess } from '../lib/rpc';
import { externalRequestKey } from '../teacher/external-model';
import { t } from '../messages';
import { AppWindow } from '../window/AppWindow';
import { submitExternalForm } from './launch';
import './environments.css';

interface Environment {
  id: string;
  label: string;
  template_label: string;
  observed?: unknown;
  expectedVersion?: string | null;
  hostname: string | null;
  status: 'queued' | 'creating' | 'ready' | 'stopped' | 'failed' | 'archived';
  jobs: ExternalJob[] | null;
}
const statuses = {
  queued: 'cms.status.queued',
  creating: 'cms.status.creating',
  ready: 'cms.status.ready',
  stopped: 'cms.status.stopped',
  failed: 'cms.status.failed',
  archived: 'cms.status.archived',
} as const;

export function EnvironmentsPage(): JSX.Element {
  const { workspace, me, observing, openTeacher } = useDesk();
  const online = useOnline();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const keys = useRef(new Map<string, string>());
  const readOnly = observing !== null || workspace?.readOnly === true;
  const environments = useQuery({
    queryKey: ['core', 'external.list', workspace?.workspaceId, workspace?.generation],
    enabled: Boolean(workspace),
    queryFn: () =>
      callCore<{ environments: Environment[] }>('external.list', {
        workspaceId: workspace!.workspaceId,
      }),
    refetchInterval: 5000,
  });
  const unavailable = !online || environments.isError;

  async function start(environment: Environment) {
    if (readOnly || busy || unavailable) return;
    const scope = `ndrc-cms-start:${me.id}:${environment.id}`;
    let key = keys.current.get(scope) ?? crypto.randomUUID();
    try {
      key = externalRequestKey(sessionStorage, scope, () => key);
    } catch {
      /* Memory retry remains available. */
    }
    keys.current.set(scope, key);
    setBusy(environment.id);
    setError('');
    try {
      await mutateCore(
        'external.command',
        { environmentId: environment.id, action: 'start', requestKey: key },
        key,
      );
      keys.current.delete(scope);
      try {
        sessionStorage.removeItem(scope);
      } catch {
        /* Storage may be unavailable. */
      }
      await environments.refetch();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t('environments.error'));
    } finally {
      setBusy(null);
    }
  }
  async function launch(environment: Environment) {
    if (readOnly || busy || unavailable) return;
    // Open during the click, before requesting the one-use ticket, to preserve the user gesture.
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setError(t('environments.popup'));
      return;
    }
    popup.opener = null;
    popup.document.documentElement.lang = 'fr';
    popup.document.title = t('environments.title');
    popup.document.body.textContent = t('environments.opening');
    setBusy(environment.id);
    setError('');
    try {
      const form = await requestExternalAccess(environment.id);
      if (popup.closed) return;
      submitExternalForm(popup, form, environment.hostname);
    } catch (failure) {
      popup.close();
      setError(failure instanceof Error ? failure.message : t('environments.error'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <AppWindow title={t('environments.title')}>
      <div className="environments-page">
        <section className="environments-content">
          <header className="environments-head">
            <div>
              <h2>{t('environments.title')}</h2>
              <p>{t('environments.hint')}</p>
            </div>
            <button
              type="button"
              className="ndrc-button"
              onClick={() => {
                void environments.refetch();
              }}
              disabled={!workspace || !online || environments.isFetching}
            >
              {t('environments.refresh')}
            </button>
          </header>
          <SubmissionsPanel />
          {readOnly && <p className="environments-notice">{t('environments.observation')}</p>}
          {error && (
            <p className="ndrc-error" role="alert">
              {error}
            </p>
          )}
          {workspace && unavailable && (
            <p className="ndrc-error" role="alert">
              {environments.data ? t('environments.cached') : t('environments.loadError')}
            </p>
          )}
          {!workspace ? (
            <p>{t('environments.unavailable')}</p>
          ) : environments.isPending ? (
            <p role="status">{t('environments.loading')}</p>
          ) : !environments.data ? null : environments.data.environments.length === 0 ? (
            <EmptyState title={t('environments.emptyTitle')} hint={t('environments.emptyHint')} />
          ) : (
            <ul className="environments-list">
              {environments.data.environments.map((environment) => {
                const pending =
                  environment.status === 'queued' ||
                  environment.status === 'creating' ||
                  environment.jobs?.some(
                    (job) => job.status === 'queued' || job.status === 'running',
                  );
                return (
                  <li key={environment.id} className="environment-card">
                    <div className="environment-heading">
                      <span className="environment-glyph">
                        <Icon name="window" size={22} />
                      </span>
                      <div>
                        <p className="environment-template">{environment.template_label}</p>
                        <h3>{environment.label}</h3>
                      </div>
                      <span className="environment-state" data-state={environment.status}>
                        {pending
                          ? t(
                              environment.status === 'queued' || environment.status === 'creating'
                                ? 'environments.pending'
                                : 'environments.operationPending',
                            )
                          : t(statuses[environment.status])}
                      </span>
                    </div>
                    {environment.hostname && (
                      <p className="environment-host">{environment.hostname}</p>
                    )}
                    <EnvironmentInventory
                      observed={environment.observed}
                      expectedVersion={environment.expectedVersion}
                    />
                    {environment.status === 'failed' && (
                      <p className="environments-notice">{t('environments.failed')}</p>
                    )}
                    <div className="environment-actions">
                      {environment.status === 'ready' && (
                        <button
                          type="button"
                          className="ndrc-button ndrc-button--primary"
                          disabled={readOnly || unavailable || Boolean(busy) || Boolean(pending)}
                          onClick={() => {
                            void launch(environment);
                          }}
                        >
                          {busy === environment.id
                            ? t('environments.opening')
                            : t('environments.open')}
                          <Icon name="arrow-right" size={14} />
                        </button>
                      )}
                      {environment.status === 'stopped' && (
                        <button
                          type="button"
                          className="ndrc-button ndrc-button--primary"
                          disabled={readOnly || unavailable || Boolean(busy) || Boolean(pending)}
                          onClick={() => {
                            void start(environment);
                          }}
                        >
                          {t('environments.start')}
                        </button>
                      )}
                      {environment.status === 'ready' && !pending && (
                        <span>{t('environments.newTab')}</span>
                      )}
                    </div>
                    <ProjectionPanel
                      environmentId={environment.id}
                      jobs={environment.jobs ?? []}
                      jobsUnavailable={unavailable}
                      canSync={
                        !readOnly && !unavailable && !pending && environment.status === 'ready'
                      }
                      onQueued={() => environments.refetch()}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          {me.role === 'teacher' && !readOnly && (
            <div>
              <button
                type="button"
                className="ndrc-button"
                onClick={() => openTeacher('environments')}
              >
                {t('environments.manage')}
              </button>
            </div>
          )}
        </section>
      </div>
    </AppWindow>
  );
}
